
import { useEffect, useRef } from "react";
import { Server, Database, Zap, Shield, Clock, Users, Activity, LayoutGrid } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const SolutionCard = ({ icon: Icon, title, description }: { icon: any; title: string; description: string }) => {
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-subtle flex flex-col items-center text-center transition-all duration-300 hover:shadow-subtle-lg hover:-translate-y-1">
      <div className="w-14 h-14 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-primary" />
      </div>
      <h3 className="font-bold text-lg mb-2 text-gray-800 dark:text-gray-100">{title}</h3>
      <p className="text-gray-600 dark:text-gray-300">{description}</p>
    </div>
  );
};

const Solution = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elementsToObserve = sectionRef.current?.querySelectorAll(".reveal-on-scroll");
    elementsToObserve?.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      elementsToObserve?.forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} id="solution" className="py-20 bg-gradient-to-b from-white dark:from-gray-900 to-blue-50 dark:to-gray-850">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16 reveal-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800 dark:text-gray-50">{t('solution_title')}</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            {t('solution_subtitle')}
          </p>
          <div className="mt-8 space-y-4 max-w-3xl mx-auto">
            <div className="p-4 bg-primary/5 dark:bg-primary/10 rounded-lg">
              <p className="dark:text-gray-200">{t('solution_point1')}</p>
            </div>
            <div className="p-4 bg-primary/5 dark:bg-primary/10 rounded-lg">
              <p className="dark:text-gray-200">{t('solution_point2')}</p>
            </div>
            <div className="p-4 bg-primary/5 dark:bg-primary/10 rounded-lg">
              <p className="dark:text-gray-200">{t('solution_point3')}</p>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 reveal-on-scroll">
          <SolutionCard 
            icon={Zap} 
            title={t('solution_card1_title')} 
            description={t('solution_card1_desc')}
          />
          <SolutionCard 
            icon={Database} 
            title={t('solution_card2_title')} 
            description={t('solution_card2_desc')}
          />
          <SolutionCard 
            icon={LayoutGrid} 
            title={t('solution_card3_title')} 
            description={t('solution_card3_desc')}
          />
          <SolutionCard 
            icon={Server} 
            title={t('solution_card4_title')} 
            description={t('solution_card4_desc')}
          />
          <SolutionCard 
            icon={Clock} 
            title={t('solution_card5_title')} 
            description={t('solution_card5_desc')}
          />
          <SolutionCard 
            icon={Users} 
            title={t('solution_card6_title')} 
            description={t('solution_card6_desc')}
          />
          <SolutionCard 
            icon={Activity} 
            title={t('solution_card7_title')} 
            description={t('solution_card7_desc')}
          />
          <SolutionCard 
            icon={Shield} 
            title={t('solution_card8_title')} 
            description={t('solution_card8_desc')}
          />
        </div>
      </div>
    </section>
  );
};

export default Solution;
